import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MainLayout } from '../components/layout/MainLayout';
import { CabecalhoPagina, FaixaFiltros } from '../components/layout/CabecalhoPagina';
import { BarraAcoes } from '../components/common/Formulario';
import { federationApi, fileApi } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { ImageUpload } from '../components/common/ImageUpload';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import DialogoMotivo from '../components/common/DialogoMotivo';
import { 
  Building2, 
  Plus, 
  Search, 
  Loader2,
  Edit,
  Trash2,
  Settings2,
  MapPin,
  Phone,
  Mail,
  User,
  ShieldCheck,
  Clock,
  CheckCircle2,
  XCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { EstadoVazio } from '../components/common/Estados';

export default function FederationsPage() {
  const [federations, setFederations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingFederation, setEditingFederation] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pendingLogo, setPendingLogo] = useState(null);

  // Vínculo do líder com a federação (Fluxo D): o poder vem do VÍNCULO, não do papel
  const { user: currentUser } = useAuth();
  const souSuperAdmin = currentUser?.role === 'super_admin';
  const [minha, setMinha] = useState(null);
  const [fila, setFila] = useState({ federations: [], requests: [] });
  const [decidindo, setDecidindo] = useState('');
  // Fase 2 (item 7): federação/vínculo aguardando o motivo da recusa.
  const [pedidoRecusa, setPedidoRecusa] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    cnpj: '',
    address: '',
    city: '',
    state: '',
    responsible: '',
    phone: '',
    email: '',
    logo_path: ''
  });

  const fetchFederations = async () => {
    try {
      const { data } = await federationApi.list();
      setFederations(data);
      try {
        const { data: meu } = await federationApi.mine();
        setMinha(meu);
      } catch (e) {
        setMinha(null);            // sem vínculo (ou sem permissão): segue sem
      }
      if (souSuperAdmin) {
        const { data: pendentes } = await federationApi.pending();
        setFila(pendentes || { federations: [], requests: [] });
      }
    } catch (error) {
      toast.error('Erro ao carregar federações');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFederations();
  }, []);

  const resetForm = () => {
    setFormData({
      name: '',
      cnpj: '',
      address: '',
      city: '',
      state: '',
      responsible: '',
      phone: '',
      email: '',
      logo_path: ''
    });
    setEditingFederation(null);
    setPendingLogo(null);
  };

  const handleEdit = (federation) => {
    setFormData({
      name: federation.name || '',
      cnpj: federation.cnpj || '',
      address: federation.address || '',
      city: federation.city || '',
      state: federation.state || '',
      responsible: federation.responsible || '',
      phone: federation.phone || '',
      email: federation.email || '',
      logo_path: federation.logo_path || ''
    });
    setEditingFederation(federation);
    setPendingLogo(null);
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir esta federação?')) return;
    
    try {
      await federationApi.delete(id);
      toast.success('Federação excluída com sucesso');
      fetchFederations();
    } catch (error) {
      toast.error('Erro ao excluir federação');
    }
  };

  // ---- Vínculo do líder (Fluxo D): as decisões são do Super Admin ----
  const aprovarFederacao = async (federacao) => {
    setDecidindo(federacao._id);
    try {
      await federationApi.approve(federacao._id);
      toast.success(`${federacao.name} aprovada — agora ela pode analisar documentos`);
      fetchFederations();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Erro ao aprovar a federação');
    } finally {
      setDecidindo('');
    }
  };

  // Fase 2 (item 7): o motivo passou a ser pedido em diálogo do sistema, com o
  // MESMO efeito de antes — cancelar não recusa nada (era o
  // `if (motivo === null) return;`) e confirmar sem texto vale como "sem
  // motivo". Mesmas APIs, mesmas mensagens de sucesso/erro de antes.
  const recusarFederacao = (federacao) => setPedidoRecusa({ tipo: 'federacao', alvo: federacao });

  const aplicarRecusa = async ({ tipo, alvo }, motivo) => {
    setPedidoRecusa(null);
    const id = alvo._id;
    setDecidindo(id);
    try {
      if (tipo === 'federacao') {
        await federationApi.reject(id, { reason: motivo });
        toast.success('Federação recusada — o responsável pode corrigir e reenviar');
      } else {
        await federationApi.rejectRequest(id, { reason: motivo });
        toast.success('Vínculo recusado');
      }
      fetchFederations();
    } catch (error) {
      toast.error(error?.response?.data?.detail
        || (tipo === 'federacao' ? 'Erro ao recusar a federação' : 'Erro ao recusar o vínculo'));
    } finally {
      setDecidindo('');
    }
  };

  const reenviarMinhaFederacao = async () => {
    const id = minha?.federation?._id;
    if (!id) return;
    setDecidindo(id);
    try {
      await federationApi.resubmit(id);
      toast.success('Federação reenviada para análise');
      fetchFederations();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Erro ao reenviar a federação');
    } finally {
      setDecidindo('');
    }
  };

  const aprovarPedido = async (pedido) => {
    setDecidindo(pedido._id);
    try {
      const { data } = await federationApi.approveRequest(pedido._id);
      toast.success(
        `${pedido.user_name} vinculado como ${data.role === 'owner' ? 'responsável' : 'analista'}`,
      );
      fetchFederations();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Erro ao aprovar o vínculo');
    } finally {
      setDecidindo('');
    }
  };

  const recusarPedido = (pedido) => setPedidoRecusa({ tipo: 'vinculo', alvo: pedido });

  const handleLogoUpload = (pathOrFile) => {
    if (pathOrFile instanceof File) {
      setPendingLogo(pathOrFile);
    } else {
      setFormData(prev => ({ ...prev, logo_path: pathOrFile }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      let result;
      if (editingFederation) {
        await federationApi.update(editingFederation._id, formData);
        result = { _id: editingFederation._id };
        toast.success('Federação atualizada com sucesso');
      } else {
        const { data: newFed } = await federationApi.create(formData);
        result = newFed;
        toast.success('Federação criada com sucesso');
      }

      // Upload pending logo if exists
      if (pendingLogo && result._id) {
        try {
          await fileApi.upload(pendingLogo, 'federation', result._id);
        } catch (err) {
          toast.error('Erro ao enviar logo');
        }
      }

      setIsDialogOpen(false);
      resetForm();
      fetchFederations();
    } catch (error) {
      toast.error(editingFederation ? 'Erro ao atualizar federação' : 'Erro ao criar federação');
    } finally {
      setSaving(false);
    }
  };

  const filteredFederations = federations.filter(f =>
    f.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="federations-page">
        {/* Header — título/contexto à esquerda, ação à direita */}
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <CabecalhoPagina
            className="min-w-0 flex-1"
            contexto="Cadastros"
            titulo="Federações"
            descricao="Gerencie as federações de Jiu-Jitsu"
            icone={Building2}
          />
          {(souSuperAdmin || minha?.federation) && (
          <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
            {/* "Nova Federação" é só do Super Admin. O líder abre ESTE MESMO
                formulário pelo botão "Editar dados" — a federação dele não
                aparece na lista pública enquanto está pendente ou recusada. */}
            {souSuperAdmin && (
            <DialogTrigger asChild>
              <Button
                data-testid="new-federation-btn"
                className="bg-[#D4AF37] text-black hover:bg-[#E5C158] font-bold"
              >
                <Plus size={20} className="mr-2" />
                Nova Federação
              </Button>
            </DialogTrigger>
            )}
            <DialogContent className="bg-[#141414] border-[#27272A] max-w-lg">
              <DialogHeader>
                <DialogTitle className="text-white font-['Chivo']">
                  {editingFederation ? 'Editar Federação' : 'Nova Federação'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <p className="text-xs text-[#A1A1AA]"><span className="text-[#D4AF37]">*</span> campos obrigatórios</p>
                <div className="space-y-2">
                  <Label className="text-[#A1A1AA]" htmlFor="federation-name">Nome *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Nome da federação"
                    required
                    id="federation-name" data-testid="federation-name"
                    className="bg-[#1A1A1A] border-[#27272A] text-white"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-[#A1A1AA]" htmlFor="federation-cnpj">CNPJ</Label>
                    <Input
                      value={formData.cnpj}
                      onChange={(e) => setFormData({ ...formData, cnpj: e.target.value })}
                      placeholder="00.000.000/0000-00"
                      inputMode="numeric"
                      maxLength={18}
                      id="federation-cnpj" data-testid="federation-cnpj"
                      className="bg-[#1A1A1A] border-[#27272A] text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#A1A1AA]" htmlFor="federation-responsible">Responsável</Label>
                    <Input
                      value={formData.responsible}
                      onChange={(e) => setFormData({ ...formData, responsible: e.target.value })}
                      placeholder="Nome do responsável"
                      id="federation-responsible" data-testid="federation-responsible"
                      className="bg-[#1A1A1A] border-[#27272A] text-white"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-[#A1A1AA]" htmlFor="federation-address">Endereço</Label>
                  <Input
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder="Endereço completo"
                    id="federation-address" data-testid="federation-address"
                    className="bg-[#1A1A1A] border-[#27272A] text-white"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-[#A1A1AA]" htmlFor="federation-city">Cidade</Label>
                    <Input
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      placeholder="Cidade"
                      id="federation-city" data-testid="federation-city"
                      className="bg-[#1A1A1A] border-[#27272A] text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#A1A1AA]" htmlFor="federation-state">Estado</Label>
                    <Input
                      value={formData.state}
                      onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                      placeholder="UF"
                      maxLength={2}
                      id="federation-state" data-testid="federation-state"
                      className="bg-[#1A1A1A] border-[#27272A] text-white"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-[#A1A1AA]" htmlFor="federation-phone">Telefone</Label>
                    <Input
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="(00) 00000-0000"
                      inputMode="tel"
                      maxLength={20}
                      id="federation-phone" data-testid="federation-phone"
                      className="bg-[#1A1A1A] border-[#27272A] text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#A1A1AA]" htmlFor="federation-email">Email</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="email@federacao.com"
                      id="federation-email" data-testid="federation-email"
                      className="bg-[#1A1A1A] border-[#27272A] text-white"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-[#A1A1AA]">Logo da Federação</Label>
                  <div className="flex items-start gap-4">
                    <ImageUpload
                      entityType="federation"
                      entityId={editingFederation?._id}
                      currentPath={formData.logo_path}
                      onUpload={handleLogoUpload}
                      shape="square"
                      label="Upload Logo"
                    />
                    <p className="text-xs text-[#71717A] mt-2">
                      Logo quadrado recomendado (200x200 pixels)
                    </p>
                  </div>
                </div>
                <BarraAcoes contexto="cartao">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => { setIsDialogOpen(false); resetForm(); }}
                    className="border-[#27272A] text-[#A1A1AA]"
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    disabled={saving}
                    data-testid="save-federation"
                    className="bg-[#D4AF37] text-black hover:bg-[#E5C158]"
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingFederation ? 'Salvar' : 'Criar')}
                  </Button>
                </BarraAcoes>
              </form>
            </DialogContent>
          </Dialog>
          )}
        </div>

        {/* AGUARDANDO APROVAÇÃO (Super Admin): federações novas + pedidos de vínculo */}
        {souSuperAdmin && (fila.federations.length > 0 || fila.requests.length > 0) && (
          <Card className="bg-[#141414] border-[#D4AF37]/40" data-testid="fila-federacoes">
            <CardContent className="p-6 space-y-4">
              <h2 className="text-lg font-bold text-white flex items-center gap-2">
                <Clock className="w-5 h-5 text-[#D4AF37]" /> Aguardando aprovação
              </h2>

              {fila.federations.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs uppercase tracking-wider text-[#71717A]">Federações novas</p>
                  <ul className="divide-y divide-[#27272A]">
                    {fila.federations.map((f) => (
                      <li key={f._id} className="flex items-center justify-between py-3 gap-3">
                        <div>
                          <p className="text-white">{f.name}</p>
                          <p className="text-xs text-[#71717A]">
                            {f.cnpj || 'sem CNPJ'}
                            {f.city ? ` · ${f.city}${f.state ? ` - ${f.state}` : ''}` : ''}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" onClick={() => aprovarFederacao(f)}
                                  disabled={decidindo === f._id}
                                  data-testid={`aprovar-federacao-${f._id}`}
                                  className="bg-[#D4AF37] text-black hover:bg-[#E5C158] font-bold">
                            <CheckCircle2 size={16} className="mr-1" /> Aprovar
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => recusarFederacao(f)}
                                  disabled={decidindo === f._id}
                                  data-testid={`recusar-federacao-${f._id}`}
                                  className="border-[#27272A] text-red-400 hover:text-red-300">
                            <XCircle size={16} className="mr-1" /> Recusar
                          </Button>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {fila.requests.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs uppercase tracking-wider text-[#71717A]">Pedidos de vínculo</p>
                  <ul className="divide-y divide-[#27272A]">
                    {fila.requests.map((p) => (
                      <li key={p._id} className="flex items-center justify-between py-3 gap-3">
                        <div>
                          <p className="text-white">
                            {p.user_name} <span className="text-[#71717A]">({p.user_email})</span>
                          </p>
                          <p className="text-xs text-[#71717A]">
                            quer responder por <strong>{p.federation_name}</strong>
                            {p.note ? ` · ${p.note}` : ''}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" onClick={() => aprovarPedido(p)}
                                  disabled={decidindo === p._id}
                                  data-testid={`aprovar-vinculo-${p._id}`}
                                  className="bg-[#D4AF37] text-black hover:bg-[#E5C158] font-bold">
                            <CheckCircle2 size={16} className="mr-1" /> Aprovar
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => recusarPedido(p)}
                                  disabled={decidindo === p._id}
                                  data-testid={`recusar-vinculo-${p._id}`}
                                  className="border-[#27272A] text-red-400 hover:text-red-300">
                            <XCircle size={16} className="mr-1" /> Recusar
                          </Button>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* AVISO DO LÍDER: ainda sem vínculo */}
        {!souSuperAdmin && !minha?.federation && (
          <Card className="bg-amber-500/5 border-amber-500/30" data-testid="aviso-sem-vinculo">
            <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
              <p className="text-sm text-amber-200">
                Você ainda não está vinculado a nenhuma federação.
              </p>
              <Button asChild size="sm" className="bg-[#D4AF37] text-black hover:bg-[#E5C158] font-bold">
                <Link to="/federacoes/vincular">Vincular minha federação</Link>
              </Button>
            </CardContent>
          </Card>
        )}

        {/* SELO DO LÍDER: a minha federação e a situação dela */}
        {!souSuperAdmin && minha?.federation && (
          <Card
            className={`bg-[#141414] ${minha.federation.status === 'recusada' ? 'border-red-500/40' : 'border-[#27272A]'}`}
            data-testid="selo-minha-federacao"
          >
            <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
              <div className="flex items-start gap-3">
                {minha.federation.valendo ? (
                  <ShieldCheck className="w-5 h-5 text-emerald-400" />
                ) : minha.federation.status === 'recusada' ? (
                  <XCircle className="w-5 h-5 text-red-400" />
                ) : (
                  <Clock className="w-5 h-5 text-amber-300" />
                )}
                <div>
                  <p className="text-sm text-[#A1A1AA]">
                    Você responde por <strong className="text-white">{minha.federation.name}</strong> —{' '}
                    {minha.federation.valendo
                      ? 'verificada (já pode analisar documentos).'
                      : minha.federation.status === 'recusada'
                        ? 'recusada pelo Super Admin.'
                        : 'aguardando o ok do Super Admin.'}
                  </p>
                  {minha.federation.status === 'recusada' && minha.federation.rejected_reason && (
                    <p className="text-xs text-red-300 mt-1" data-testid="motivo-recusa">
                      Motivo: {minha.federation.rejected_reason}
                    </p>
                  )}
                  {minha.federation.status === 'recusada' && (
                    <p className="text-xs text-[#71717A] mt-1">
                      Corrija os dados com o botão Editar e reenvie para análise.
                    </p>
                  )}
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <Button size="sm" variant="outline" onClick={() => handleEdit(minha.federation)}
                        data-testid="editar-minha-federacao"
                        className="border-[#27272A] text-[#A1A1AA] hover:text-white">
                  <Edit size={16} className="mr-1" /> Editar dados
                </Button>
                {minha.federation.status === 'recusada' && (
                  <Button size="sm" onClick={reenviarMinhaFederacao}
                          disabled={decidindo === minha.federation._id}
                          data-testid="reenviar-federacao"
                          className="bg-[#D4AF37] text-black hover:bg-[#E5C158] font-bold">
                    <CheckCircle2 size={16} className="mr-1" /> Corrigir e reenviar
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filtros em faixa própria (Etapa Visual 2) */}
        <FaixaFiltros rotulo="Buscar federações">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A]" size={20} />
          <Input
            placeholder="Buscar federações..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="search-federations"
            className="pl-10 bg-[#141414] border-[#27272A] text-white placeholder:text-[#71717A]"
          />
        </div>
        </FaixaFiltros>

        {/* Federations Grid */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" aria-hidden="true" /><span role="status" aria-live="polite" className="ml-2 text-sm text-[#A1A1AA]">Carregando...</span>
          </div>
        ) : filteredFederations.length === 0 ? (
          <Card className="bg-[#141414] border-[#27272A]">
            <CardContent>
              <EstadoVazio icone={Building2} titulo="Nenhuma federação encontrada" descricao="Crie sua primeira federação para começar" />
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredFederations.map((federation) => (
              <Card 
                key={federation._id}
                className="bg-[#141414] border-[#27272A] card-hover"
                data-testid={`federation-card-${federation._id}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center">
                        <Building2 className="w-6 h-6 text-[#D4AF37]" />
                      </div>
                      <div>
                        <h3 className="font-bold text-white">{federation.name}</h3>
                        {federation.cnpj && (
                          <p className="text-xs text-[#71717A]">{federation.cnpj}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2 text-sm mb-4">
                    {federation.city && (
                      <div className="flex items-center gap-2 text-[#A1A1AA]">
                        <MapPin size={16} className="text-[#D4AF37]" />
                        <span>{federation.city}{federation.state && ` - ${federation.state}`}</span>
                      </div>
                    )}
                    {federation.responsible && (
                      <div className="flex items-center gap-2 text-[#A1A1AA]">
                        <User size={16} className="text-[#D4AF37]" />
                        <span>{federation.responsible}</span>
                      </div>
                    )}
                  </div>

                  <div className="flex gap-2">
                    {(souSuperAdmin || federation._id === minha?.federation?._id) && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(federation)}
                      data-testid={`editar-${federation._id}`}
                      className="flex-1 border-[#27272A] text-[#A1A1AA] hover:text-white"
                    >
                      <Edit size={16} className="mr-1" /> Editar
                    </Button>
                    )}
                    {(souSuperAdmin || federation._id === minha?.federation?._id) && (
                    <Button
                      variant="outline"
                      size="sm"
                      asChild
                    >
                      <Link
                        to={`/ranking-federativo/regras?federationId=${federation._id}`}
                        data-testid={`configure-rules-${federation._id}`}
                        className="flex-1 border-[#27272A] text-[#A1A1AA] hover:text-white"
                      >
                        <Settings2 size={16} className="mr-1" /> Configurar Regras
                      </Link>
                    </Button>
                    )}
                    {souSuperAdmin && (
                    <>
                    {/* Separador (Fase 2, item 10): exclusão afastada das ações normais */}
                    <div className="w-px self-stretch bg-[#27272A] mx-1" aria-hidden="true" />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(federation._id)}
                      data-testid={`excluir-${federation._id}`}
                      aria-label="Excluir"
                      className="border-[#27272A] text-red-400 hover:text-red-300 hover:border-red-400/50"
                    >
                      <Trash2 size={16} />
                    </Button>
                    </>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      <DialogoMotivo
        aberto={!!pedidoRecusa}
        titulo={pedidoRecusa?.tipo === 'federacao'
          ? `Motivo da recusa de ${pedidoRecusa?.alvo?.name}:`
          : `Motivo da recusa do vínculo de ${pedidoRecusa?.alvo?.user_name}:`}
        descricao={pedidoRecusa?.tipo === 'federacao'
          ? 'A federação volta para o responsável junto desta mensagem.'
          : 'A pessoa que pediu o vínculo verá esta mensagem.'}
        rotulo="Motivo"
        placeholder={pedidoRecusa?.tipo === 'federacao'
          ? 'Ex.: o CNPJ não confere com o comprovante enviado'
          : 'Ex.: você ainda não é aluno desta equipe'}
        textoConfirmar="Recusar"
        varianteConfirmar="destructive"
        aoConfirmar={(texto) => pedidoRecusa && aplicarRecusa(pedidoRecusa, texto)}
        aoCancelar={() => setPedidoRecusa(null)}
        testId="dialogo-recusar"
      />
    </MainLayout>
  );
}
